import { ContentService } from '@blk/cwp-ng-framework';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as utc from 'dayjs/plugin/utc';
import * as defaultTo from 'lodash/defaultTo';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { ColumnText } from '../column-text';
import { NumberToPercentFormatter } from '../formatters/number-to-percent-formatter';
import { TableData } from '../table-data';
import { TableDataTransform } from './table-data-transform';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export class YearOnYearPerformanceDataTransform implements TableDataTransform {
	public readonly MONTHS_FOR_5_YEARS = 60;
	public readonly MONTHS_IN_YEAR = 12;

	public transform(data: TableData, contentService?: ContentService): TableData {
		let cellHeadersX: CellData[] = [];
		const cellData: CellData[][] = [];

		get(data, 'cells', []).forEach(portfolio => {
			let startDate: string;
			let stopDate: string;
			cellHeadersX = [];
			const cellDataPerPortfolio: CellData[] = [];

			// Full 10 year guaranteed backfilled performance data
			const fullTimeSeriesSample = get(portfolio, '[0].rawValue', []);
			// Get the latest 5 years of the timeseries sample
			const fiveYearSample = fullTimeSeriesSample.slice(-this.MONTHS_FOR_5_YEARS, fullTimeSeriesSample.length);

			fiveYearSample.forEach((value, i) => {
				// Gets the full years of timeseries (monthly) data. So 12 items in the array and 5 arrays due to the 5 years worth of data.
				const perYear = i % this.MONTHS_IN_YEAR ? [] : fiveYearSample.slice(i, i + this.MONTHS_IN_YEAR);

				if (!isEmpty(perYear)) {
					// Format the dates for the chart and table
					const rawStartDate = get(perYear, '[0].interval.stopDate');
					const rawStopDate = get(perYear, '[11].interval.stopDate');
					startDate = rawStartDate ? dayjs.utc(rawStartDate, 'YYYY-MM-DD').format('MM/YY') : '';
					stopDate = rawStopDate ? dayjs.utc(rawStopDate, 'YYYY-MM-DD').format('MM/YY') : '';
					const formattedDate = startDate + '-' + stopDate;

					// Create the date cells from 1-5 years
					const dateCell = new CellData(
						new ColumnConfig(
							new ColumnText('YEAR_ON_YEAR_DATE', 'Year on Year Date'),
							null,
							null
						),
						formattedDate,
						formattedDate
					);

					const annualizedReturnRolling12Mo = perYear[11].rollingExPostMetrics?.find(
						rollingExPostMetricsValue => rollingExPostMetricsValue.name === 'annualizedReturnRolling12Mo');

					const formattedRollingReturnsData = new NumberToPercentFormatter().format(
						Number.parseFloat(annualizedReturnRolling12Mo?.metricValue)
					);

					// Create the annualized return cells with formatted value being a percentage
					const rollingTwelveMonthsCell = new CellData(
						new ColumnConfig(
							new ColumnText('ANNUALIZED_RETURN', 'Annualized Return'),
							null,
							null
						),
						defaultTo(annualizedReturnRolling12Mo?.metricValue, '0'),
						formattedRollingReturnsData === 'NaN%' ? '-' : formattedRollingReturnsData
					);
					cellHeadersX.push(dateCell);
					cellDataPerPortfolio.push(rollingTwelveMonthsCell);
				}
			});
			cellDataPerPortfolio.push(...portfolio.slice(1));
			cellData.push(cellDataPerPortfolio);
		});

		cellHeadersX.push(...data.headersX.slice(1));
		return new TableData(cellHeadersX, data.headersY, cellData, data.portfolioData, data.firstColumnTitle);
	}
}
